import { Button } from '@avanta/shared/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@avanta/shared/components/ui/DropdownMenu';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import {
  useActiveConversation,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { cn } from '@avanta/shared/utils/cn';
import { Archive, ArchiveRestore, CircleX, EllipsisVertical, Info, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { CHAT_BUTTON_CLASSNAME } from 'pages/Chat/components';
import { useIsPatientOrEmployee, useIsPsychologist, useIsSupporter } from 'stores/user';
import { isEmotionalSupport } from 'utils/chat';
import { openInNewTab } from 'utils/common';
import { LeaveConversationDialog } from './LeaveConversationDialog';

export const Header = (): React.ReactElement => {
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const isOpenInfo = useConversationsStore(state => state.isOpenInfo);
  const isOtherUserOnline = useConversationsStore(state => state.isOtherUserOnline);
  const leaveConversation = useConversationsStore(state => state.leaveConversation);
  const archiveConversation = useConversationsStore(state => state.archiveConversation);
  const setActiveChannel = useConversationsStore(state => state.setActiveChannel);
  const unarchiveConversation = useConversationsStore(state => state.unarchiveConversation);
  const setIsOpenInfo = useConversationsStore(state => state.setIsOpenInfo);
  const activeConversation = useActiveConversation();
  const isArchived = activeConversation?.membership?.custom?.archived === true;

  const navigate = useNavigate();
  const isPatientOrEmployee = useIsPatientOrEmployee();
  const isSupporter = useIsSupporter();
  const isPsychologist = useIsPsychologist();
  const showInfoAction = isSupporter || isPsychologist;
  const showArchiveAction = isSupporter || isPsychologist;
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const { showDialog } = useDialogContext();

  const closeConversation = (): void => {
    setIsOpenInfo(false);
    setActiveChannel(null);
  };

  const handleLeaveConversation = (): void => {
    leaveConversation(activeChannelId ?? '');
    closeConversation();
    if (isEmotionalSupport) {
      const params = new URLSearchParams({
        name: activeConversation?.otherUser.name ?? '',
        patientId: activeConversation?.otherUser.id ?? '',
      });
      openInNewTab(`${ROUTES.CHAT_REPORT}?${params.toString()}`);
    }
    toast.success(t('Chat.leave.success'));
  };

  const handleOpenLeaveConversationDialog = (): void => {
    showDialog({
      component: <LeaveConversationDialog onConfirm={handleLeaveConversation} />,
    });
  };

  const handleArchiveConversation = (): void => {
    archiveConversation(activeChannelId ?? '').then(() => {
      closeConversation();
      archiveConversation(activeChannelId ?? '');
      toast.success(t('Chat.archive.success'));
    });
  };

  const handleUnarchiveConversation = (): void => {
    unarchiveConversation(activeChannelId ?? '').then(() => {
      closeConversation();
      unarchiveConversation(activeChannelId ?? '');
      toast.success(t('Chat.unarchive.success'));
    });
  };

  const handleOnClose = (): void => {
    closeConversation();
    if (isPatientOrEmployee) {
      navigate(-1);
    }
  };

  return (
    <div className="flex justify-between items-center p-4 border-b border-plum-200 bg-white sticky top-0 left-0 right-0 z-10">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium md:text-base">{activeConversation?.otherUser.name}</p>
        {isOtherUserOnline && (
          <div className="flex items-center gap-2 animate-in fade-in-0 duration-300">
            <span className="size-2 rounded-full bg-teal-500" />
            <span className="text-xs text-gray-500">{t('Chat.Header.online')}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {showInfoAction && (
          <Button
            variant="ghost"
            icon="only"
            onClick={() => setIsOpenInfo(!isOpenInfo)}
            aria-label={t('Chat.info.cta')}
            aria-pressed={isOpenInfo}
            className={cn(CHAT_BUTTON_CLASSNAME, 'hover:[&>svg]:stroke-2')}
          >
            <Info className="size-5" />
          </Button>
        )}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              icon="only"
              className={cn(CHAT_BUTTON_CLASSNAME, 'hover:[&>svg]:stroke-2')}
            >
              <EllipsisVertical className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            {showArchiveAction && (
              <>
                {isArchived ? (
                  <DropdownMenuItem
                    className="hover:bg-gray-100"
                    onClick={handleUnarchiveConversation}
                  >
                    <ArchiveRestore />
                    {t('Chat.unarchive.cta')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="hover:bg-gray-100"
                    onClick={handleArchiveConversation}
                  >
                    <Archive />
                    {t('Chat.archive.cta')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="hover:bg-gray-100"
                  onClick={handleOpenLeaveConversationDialog}
                >
                  <LogOut />
                  {t('Chat.leave.cta')}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem className="hover:bg-gray-100" onClick={handleOnClose}>
              <CircleX />
              {t('Chat.close')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
